import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Shield, Users, TrendingUp, Award, Leaf } from 'lucide-react';
import Header from './components/Header1';
import Footer from './components/Footer1';

const About = () => {
    const navigate = useNavigate();

    return (
        <div className="w-full min-h-screen bg-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            <Header />

            <main className="w-full">
                {/* Hero */}
                <section className="relative w-full min-h-[50vh] md:min-h-[60vh] overflow-hidden">
                    <img src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="About PRIYA" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#1B4332]/80 via-[#2D6A4F]/50 to-transparent" />
                    <div className="relative z-10 h-full min-h-[50vh] md:min-h-[60vh] flex items-center px-4 md:px-6 lg:px-8">
                        <div className="max-w-7xl mx-auto w-full">
                            <div className="max-w-xl">
                                <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-white/70 mb-2">Our Story</p>
                                <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-3 md:mb-4 leading-tight">About PRIYA</h1>
                                <p className="text-sm md:text-base text-white/80 leading-relaxed">Celebrating the timeless beauty of Indian craftsmanship, one handcrafted piece at a time.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Story */}
                <section className="py-12 md:py-20 px-4 md:px-6 lg:px-8 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
                            <div>
                                <img src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=600" alt="Our Story" className="w-full h-auto" />
                            </div>
                            <div>
                                <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#2D6A4F] mb-2 font-medium">Heritage</p>
                                <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#1A1A1A] mb-4 md:mb-5">Our Story</h2>
                                <div className="space-y-3 text-sm md:text-base text-[#6B7280] leading-relaxed">
                                    <p>PRIYA was born from a deep love for India's rich textile heritage. We believe that every saree, every lehenga, every kurta carries within it centuries of artisanal wisdom that deserves to be celebrated.</p>
                                    <p>Working directly with master weavers and craftspeople across India, from the silk looms of Varanasi to the chikankari artisans of Lucknow, we bring you authentic ethnic wear that honors tradition while embracing modern sensibilities.</p>
                                    <p>Every piece in our collection is a testament to the skill, patience, and artistry of the hands that created it.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Values */}
                <section className="py-12 md:py-20 px-4 md:px-6 lg:px-8 bg-[#F8FAF8]">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-8 md:mb-14">
                            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#2D6A4F] mb-2 font-medium">What We Stand For</p>
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#1A1A1A]">Our Values</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                            {[
                                { icon: Heart, title: 'Craftsmanship', desc: 'Every piece is handcrafted by skilled artisans who pour their heart into their work' },
                                { icon: Leaf, title: 'Sustainability', desc: 'We use natural fabrics and eco-friendly processes to minimize our environmental impact' },
                                { icon: Shield, title: 'Authenticity', desc: 'We work directly with weavers to ensure every product is genuine and ethically sourced' },
                                { icon: Users, title: 'Community', desc: 'We support artisan communities by providing fair wages and sustainable livelihoods' }
                            ].map((value, idx) => (
                                <div key={idx} className="bg-white p-5 md:p-6 border border-[#E5E7EB] hover:border-[#2D6A4F] transition-colors">
                                    <div className="w-10 h-10 bg-[#2D6A4F]/10 rounded-full flex items-center justify-center mb-3">
                                        <value.icon className="w-5 h-5 text-[#2D6A4F]" />
                                    </div>
                                    <h3 className="text-base font-semibold text-[#1A1A1A] mb-2">{value.title}</h3>
                                    <p className="text-sm text-[#6B7280] leading-relaxed">{value.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Stats */}
                <section className="py-12 md:py-20 px-4 md:px-6 lg:px-8 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                            {[
                                { num: '10K+', label: 'Happy Customers' },
                                { num: '200+', label: 'Artisan Partners' },
                                { num: '15+', label: 'States Covered' },
                                { num: '99%', label: 'Satisfaction Rate' }
                            ].map((stat, idx) => (
                                <div key={idx} className="text-center p-5 md:p-6 bg-[#F8FAF8]">
                                    <div className="text-2xl md:text-3xl font-semibold text-[#2D6A4F] mb-1">{stat.num}</div>
                                    <div className="text-xs md:text-sm text-[#6B7280]">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Team */}
                <section className="py-12 md:py-20 px-4 md:px-6 lg:px-8 bg-[#F8FAF8]">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-8 md:mb-14">
                            <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#2D6A4F] mb-2 font-medium">Our People</p>
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#1A1A1A]">Meet the Team</h2>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                            {[
                                { name: 'Priya Kapoor', role: 'Founder & Designer', img: 'photo-1494790108377-be9c29b29330' },
                                { name: 'Ravi Mehta', role: 'Creative Director', img: 'photo-1507003211169-0a1dd7228f2d' },
                                { name: 'Sunita Devi', role: 'Head Artisan', img: 'photo-1573497019940-1c28c88b4f3e' },
                                { name: 'Arjun Singh', role: 'Operations Lead', img: 'photo-1560250097-0b93528c311a' }
                            ].map((member, idx) => (
                                <div key={idx} className="group">
                                    <div className="relative overflow-hidden aspect-square mb-3">
                                        <img src={`https://images.unsplash.com/${member.img}?w=400&h=400&fit=crop`} alt={member.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    </div>
                                    <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A]">{member.name}</h3>
                                    <p className="text-xs md:text-sm text-[#6B7280]">{member.role}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-12 md:py-20 px-4 md:px-6 lg:px-8 bg-[#2D6A4F]">
                    <div className="max-w-xl mx-auto text-center">
                        <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3 md:mb-4">Discover Our Collection</h2>
                        <p className="text-sm md:text-base text-white/70 mb-6">Explore handcrafted ethnic wear that celebrates the beauty of Indian craftsmanship.</p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button onClick={() => navigate('/template1/shop')} className="px-6 py-3 bg-white text-[#2D6A4F] text-sm font-medium hover:bg-[#F8FAF8] transition-colors">
                                Shop Now
                            </button>
                            <button onClick={() => navigate('/template1/contact')} className="px-6 py-3 border border-white text-white text-sm font-medium hover:bg-white hover:text-[#2D6A4F] transition-colors">
                                Contact Us
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default About;
